import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuthCallback() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // Use useRef synchronously to prevent StrictMode double-invocation
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        // session_id is in URL fragment (hash), e.g. #session_id=abc123
        const hash = window.location.hash || "";
        const match = hash.match(/session_id=([^&]+)/);
        if (!match) {
          setError("세션 정보가 없습니다.");
          setTimeout(() => navigate("/"), 1500);
          return;
        }
        const sessionId = match[1];
        // Check for referral code (stored in localStorage before OAuth redirect)
        const refCode = localStorage.getItem("nc_ref_code") || "";

        const res = await fetch(`${API_URL}/api/auth/google/exchange`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId, referral_code: refCode }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "구글 로그인에 실패했습니다.");

        // Validate response shape before storing
        if (!data.access_token || !data.user) {
          console.error("[AuthCallback] Invalid exchange response", { hasToken: !!data.access_token, hasUser: !!data.user, keys: Object.keys(data || {}) });
          throw new Error("구글 로그인 응답이 올바르지 않습니다. 다시 시도해주세요.");
        }

        // Clean the referral code (consumed)
        localStorage.removeItem("nc_ref_code");

        // Store auth (same as email/password login)
        login(data.access_token, data.user);

        // Clean the URL hash
        window.history.replaceState(null, "", window.location.pathname);

        // Redirect based on onboarding status
        if (data.needs_onboarding) {
          navigate("/onboarding", { replace: true });
        } else {
          navigate("/community", { replace: true });
        }
      } catch (e) {
        setError(e.message || "구글 로그인 처리 중 오류가 발생했습니다.");
        setTimeout(() => navigate("/"), 2500);
      }
    };

    processSession();
  }, [login, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50" data-testid="auth-callback-page">
      <div className="text-center">
        {error ? (
          <div className="text-red-600">
            <p className="text-sm mb-2">{error}</p>
            <p className="text-xs text-stone-400">잠시 후 홈으로 이동합니다...</p>
          </div>
        ) : (
          <>
            <Loader2 className="w-10 h-10 text-burgundy-500 mx-auto mb-3 animate-spin" />
            <p className="text-sm text-stone-600">구글 계정으로 로그인 중...</p>
          </>
        )}
      </div>
    </div>
  );
}
